import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import { degradeMetal } from '../../components/Podium.tsx';
import type { RoundPlayer } from '../../lib/rounds.ts';

export interface LigneClassement extends RoundPlayer {
    /** Écart au par sur les trous joués. `null` avant le premier trou. */
    ecart: number | null;
}

/** Combien chaque visage laisse voir du suivant, en pixels. Assez pour qu'on
 *  compte les joueurs d'un coup d'œil, assez peu pour que le meneur reste le
 *  seul entièrement lisible. */
const PAS_PX = 26;
const TAILLE_PX = 46;

/** L'air laissé autour de la pile, en pixels.
 *
 *  Un anneau se dessine EN DEHORS de la boîte de l'élément, et le meneur en
 *  porte un de deux pixels, parfois doublé d'un liseré quand c'est vous : à
 *  `left: 0` dans un cadre taillé au ras des visages, il était rogné sur sa
 *  gauche. Le tressaillement du dépassement, qui grossit le visage de quinze
 *  pour cent, débordait de la même façon des quatre côtés.
 *
 *  Six pixels couvrent les deux : trois pour l'anneau et son liseré, presque
 *  trois pour la moitié de l'agrandissement d'un visage de trente-quatre. */
const MARGE_PX = 6;

/** Combien de temps dure le tressaillement du nouveau meneur. Assez pour être
 *  vu en marchant, assez peu pour ne pas devenir un clignotement. */
const FETE_MS = 1400;

/** L'épaisseur de l'anneau, en pixels.
 *
 *  ENTIER, comme le diamètre : l'anneau se soustrait deux fois du disque, et
 *  un demi-pixel laissait un diamètre impair qui, une fois multiplié par la
 *  densité d'un téléphone, tombait entre deux pixels physiques. Le cercle s'y
 *  aplatissait d'un côté — invisible sur une initiale, franc sur un visage. */
const ANNEAU_PX = 3;

/** Le nombre de rangs qui portent un métal. Au-delà, plus rien : le
 *  quatrième n'est pas un rang, c'est le peloton. */
const METAUX = 3;

/** L'anneau du rang. Le dégradé vient du podium, et de nulle part ailleurs :
 *  l'or de la pastille du meneur et l'or de la marche qu'il occupera au
 *  club-house sont le même or, sans quoi on croirait à deux distinctions
 *  différentes. */
function anneau(rang: number): CSSProperties {
    return { background: degradeMetal(rang) ?? 'rgb(255 255 255 / 0.45)' };
}

/* Qui mène, en haut de l'écran de jeu.
 *
 * ── Pourquoi des visages et pas un chiffre ──────────────────────────────────
 *
 * La place était occupée par « Terminer », un bouton rouge qu'on touche une
 * fois par tour et qui passait donc quatre heures à occuper le meilleur coin
 * de l'écran. Il est descendu en bas de page, où l'on sort.
 *
 * Ce qui le remplace est ce qu'on regarde vraiment quand on joue à plusieurs :
 * qui est devant. Pas un tableau — il y en a un juste en dessous — mais un
 * coup d'œil, à la place où le pouce ne va jamais.
 *
 * ── Le meneur devant, les autres derrière ───────────────────────────────────
 *
 * Les visages se chevauchent, le premier au classement entièrement visible et
 * posé PAR-DESSUS les autres, qui dépassent vers la droite. C'est la pile de
 * jetons d'un jeu de table : on lit le meneur sans rien compter, et le nombre
 * de joueurs se devine à la longueur de la pile.
 *
 * Aucun cadre autour : ce n'est pas un objet de l'interface, c'est une
 * information posée sur le fond.
 *
 * ── Pourquoi ils glissent ───────────────────────────────────────────────────
 *
 * Doubler quelqu'un est le moment du tour qu'on veut voir. Une pile qui se
 * réordonne d'un coup ne le montre pas : un visage a changé de place, et il
 * faut relire pour comprendre. En glissant, le mouvement RACONTE — et prendre
 * la tête n'est pas un dépassement comme un autre, d'où l'anneau laiton qui
 * enfle brièvement autour du nouveau premier.
 *
 * Tout cela est sous `motion-safe` : qui a demandé moins d'animations obtient
 * la même pile, immédiate.
 */
export function PileAvatars({ lignes, moi, onOuvrir }: {
    lignes: LigneClassement[];
    moi: string | null;
    /** Toucher la pile ouvre les cartes de tout le monde. Absent, elle reste
     *  une image. */
    onOuvrir?: () => void;
}) {
    /* Tout le monde figure, dès l'invitation.
     *
     * Seuls les joueurs partis comptaient : celui qui organise la partie
     * voyait donc sa pile se remplir un visage à la fois, sans jamais savoir
     * si les autres avaient reçu quoi que ce soit. Sur le premier départ, la
     * question n'est pas « qui mène » mais « sont-ils là ».
     *
     * Ils n'ont pour autant pas de RANG : le classement les met derrière ceux
     * qui jouent, ils ne portent aucun métal, et leur visage reste en gris
     * tant qu'ils n'ont pas répondu. La pile dit qui est convié ; la couleur
     * dit qui est là. */
    const partis = lignes.filter((j) => j.status !== 'declined');
    const premier = partis[0]?.ecart != null && partis[0]?.status === 'accepted'
        ? partis[0].userId : null;
    const [fete, setFete] = useState(false);
    const precedent = useRef<string | null>(null);

    useEffect(() => {
        // La première fois ne se fête pas : personne n'a été doublé, l'écran
        // vient de s'ouvrir.
        if (premier && precedent.current && premier !== precedent.current) {
            setFete(true);
            const t = setTimeout(() => setFete(false), FETE_MS);
            return () => clearTimeout(t);
        }
        precedent.current = premier;
        return undefined;
    }, [premier]);

    useEffect(() => { precedent.current = premier; }, [premier, fete]);

    if (partis.length < 2) return null;

    /* Un BOUTON, et non une image cliquable : la pile ouvre les cartes de
       tous les joueurs, c'est une action, elle doit s'atteindre au clavier et
       s'annoncer comme telle. Sans `onOuvrir` elle redevient une simple
       image — aucun rôle, aucun focus à traverser pour rien. */
    const attente = partis.filter((j) => j.status !== 'accepted').length;
    const Cadre = onOuvrir ? 'button' : 'div';
    return (
        <Cadre
            {...(onOuvrir ? { type: 'button' as const, onClick: onOuvrir } : {})}
            className="relative shrink-0 rounded-full transition-transform active:scale-95"
            style={{
                width: (partis.length - 1) * PAS_PX + TAILLE_PX + MARGE_PX * 2,
                height: TAILLE_PX + MARGE_PX * 2,
                margin: -MARGE_PX,
            }}
            aria-label={`${partis.length} joueurs${
                attente > 0 ? `, dont ${attente} sans réponse` : ''
            }, ${partis[0]?.displayName ?? ''} en tête${onOuvrir ? ' — voir les cartes' : ''}`}
        >
            {partis.map((j, rang) => {
                /* Le podium ne se donne qu'à qui a JOUÉ. Trois joueurs partis
                   dont deux n'ont pas tapé : le deuxième porterait de l'argent
                   pour n'avoir rien fait, et le rang cesserait de vouloir dire
                   quelque chose. */
                const classe = j.ecart != null && j.status === 'accepted';
                const attendu = j.status !== 'accepted';
                const podium = classe ? rang : METAUX;
                const enTete = podium === 0;
                return (
                    <span
                        key={j.userId}
                        title={attendu
                            ? `${j.displayName} · n'a pas encore répondu`
                            : `${j.displayName}${j.ecart != null ? ` · ${ecartTexte(j.ecart)}` : ''}`}
                        className="absolute rounded-full motion-safe:transition-all
                                   motion-safe:duration-500 motion-safe:ease-out"
                        style={{
                            left: MARGE_PX,
                            top: MARGE_PX,
                            zIndex: partis.length - rang,
                            transform: `translateX(${rang * PAS_PX}px) scale(${fete && enTete ? 1.15 : 1})`,
                            /* VOUS, quel que soit votre rang : un trait vert
                               posé par-dessus le métal. Il ne le remplace pas
                               — mener et être soi sont deux informations, et
                               l'une ne doit pas effacer l'autre. */
                            ...(j.userId === moi
                                ? { outline: '2px solid var(--color-primary)', outlineOffset: '1px' }
                                : {}),
                            borderRadius: '9999px',
                        }}
                    >
                        <span className={`flex rounded-full ${enTete ? 'battement-or' : ''}`}
                            style={{
                                ...anneau(podium), padding: ANNEAU_PX,
                                /* En attente : gris et en retrait. Pas absent —
                                   il faut voir qu'on l'a convié — mais pas non
                                   plus au même plan que ceux qui jouent. */
                                ...(attendu ? { filter: 'grayscale(1)', opacity: 0.5 } : {}),
                            }}>
                            <Avatar name={j.displayName} avatarUrl={j.avatarUrl}
                                size={TAILLE_PX - ANNEAU_PX * 2} />
                        </span>
                    </span>
                );
            })}
        </Cadre>
    );
}

/** L'écart tel qu'on l'annonce au golf : rien à l'égalité, un signe sinon. */
function ecartTexte(e: number): string {
    if (e === 0) return 'par';
    return e > 0 ? `+${e}` : `−${Math.abs(e)}`;
}
